import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Layers, DoorOpen, Grid3x3, Fence, PanelTop, Shield, ArrowUpRight } from "lucide-react";
import { DiamondPattern } from "./Graphics";

const services = [
  {
    icon: PanelTop,
    title: "Sliding Windows and Door Systems",
    desc: "Effortless sliding solutions designed for smooth operation, space efficiency, and modern aesthetics.",
    num: "01",
  },
  {
    icon: Grid3x3,
    title: "Casement & Tilt-Turn Window Systems",
    desc: "Engineered for maximum ventilation, superior sealing, and enhanced indoor comfort.",
    num: "02",
  },
  {
    icon: Layers,
    title: "Bi-Fold & Pivot Door Systems",
    desc: "Elegant opening systems that create seamless transitions between indoor and outdoor spaces.",
    num: "03",
  },
  {
    icon: DoorOpen,
    title: "Façade & Glazing Systems",
    desc: "High-performance glazing solutions that combine structural strength with contemporary design.",
    num: "04",
  },
  {
    icon: Fence,
    title: "Slim Partition Systems",
    desc: "High-performance glazing solutions that combine structural strength with contemporary design.",
    num: "05",
  },
  {
    icon: Shield,
    title: "Custom Architectural Aluminium Solutions",
    desc: "Tailor-made aluminium systems crafted to meet unique architectural and design requirements.",
    num: "06",
  },
];

function ServiceCard({ service, i, inView }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`service-card-${i}`}
      className="relative bg-white border border-black/5 p-8 lg:p-10 group cursor-pointer overflow-hidden"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s ease",
        boxShadow: hovered ? "0 16px 48px rgba(0,0,0,0.08), 0 0 0 1px rgba(212,175,55,0.15)" : "0 2px 8px rgba(0,0,0,0.03)",
      }}
    >
      {/* Gold border reveal on hover */}
      <motion.div
        className="absolute inset-0 border border-[#D4AF37]/40 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />

      {/* Background number */}
      <span className="absolute top-4 right-4 text-5xl font-black font-['Oswald'] text-black/[0.03] group-hover:text-[#D4AF37]/[0.08] transition-colors duration-500">
        {service.num}
      </span>

      <div className="relative z-10">
        <motion.div
          animate={hovered ? { rotate: -10, scale: 1.15 } : { rotate: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 15 }}
        >
          <service.icon
            className="w-8 h-8 text-[#D4AF37] mb-6"
            strokeWidth={1.5}
          />
        </motion.div>

        <h3 className="text-xl font-semibold font-['Oswald'] uppercase tracking-wide text-[#1A1A1A] mb-3">
          {service.title}
        </h3>
        <p className="text-sm leading-relaxed text-[#666]">
          {service.desc}
        </p>

        {/* Animated bottom line + arrow */}
        <div className="flex items-center gap-2 mt-6">
          <motion.div
            className="h-px bg-[#D4AF37]/40"
            animate={{ width: hovered ? 48 : 32 }}
            transition={{ duration: 0.4 }}
          />
          <motion.div
            animate={{ opacity: hovered ? 1 : 0, x: hovered ? 0 : -10 }}
            transition={{ duration: 0.3 }}
          >
            <ArrowUpRight size={14} className="text-[#D4AF37]" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

export default function ServicesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="services"
      ref={ref}
      data-testid="services-section"
      className="py-16 lg:py-24 bg-[#F8F8F8] relative noise-overlay"
    >
      {/* Decorative diamond pattern */}
      <DiamondPattern className="absolute top-0 right-0 w-96 h-96 hidden lg:block" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="flex items-end justify-between mb-16"
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                initial={{ scaleX: 0 }}
                animate={inView ? { scaleX: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="w-8 h-px bg-[#D4AF37] origin-left"
              />
              <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">
                What We Do
              </p>
            </div>
            <div className="overflow-hidden">
              <motion.h2
                initial={{ y: "100%" }}
                animate={inView ? { y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="text-4xl sm:text-5xl font-bold uppercase tracking-tight font-['Oswald'] text-[#1A1A1A]"
              >
                Our Services
              </motion.h2>
            </div>
          </div>
          <motion.span
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 0.05 } : {}}
            transition={{ duration: 1, delay: 0.5 }}
            className="hidden lg:block text-8xl font-black font-['Oswald'] uppercase text-white"
          >
            06
          </motion.span>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => (
            <ServiceCard key={service.title} service={service} i={i} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}
